import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';

import OperationProcessWorkflow from '../components/operation-process/OperationProcessWorkflow';
import { useWorkflowTokens } from '../components/operation-process/WorkflowStepCard';
import { buildWorkflowView } from '../components/operation-process/operationWorkflowModel';
import { buildOperationProcessPdfHtml } from '../components/operation-process/operationProcessPdf';
import {
  OperationProcessData,
  OperationProcessService,
  OperationProcessSiteInfo,
} from '../services/OperationProcessService';
import { SiteProjectService } from '../services/SiteProjectService';
import { getUserInfo } from '../utils/auth';

const EMPTY_SITE_INFO: OperationProcessSiteInfo = {
  siteName: '',
  location: '',
  manager: '',
  startDate: '',
  endDate: '',
  customer: '',
  contact: '',
  siteCode: '',
  remark: '',
};

/**
 * 현장별 운영 프로세스 페이지 — 운영 템플릿과 동일한 업무절차 워크플로 디자인.
 * 라우트: /project/:projectId/operation-process
 *
 * admin/editor 는 인라인 편집(즉시 저장), 그 외 권한은 조회 전용(readOnly).
 * PDF 다운로드 / 템플릿에서 가져오기는 상단 헤더 버튼으로 유지.
 */
export default function OperationProcessPage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const siteIdNum = projectId ? Number(projectId) : NaN;
  const user = useMemo(() => getUserInfo(), []);
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const wf = useWorkflowTokens();

  const [siteName, setSiteName] = useState<string>('');
  const [importing, setImporting] = useState(false);
  // 템플릿 가져오기 후 워크플로를 서버 상태로 다시 로드시키는 remount key.
  const [wfKey, setWfKey] = useState(0);

  useEffect(() => {
    if (Number.isNaN(siteIdNum)) return;
    SiteProjectService.getProject(siteIdNum)
      .then((p) => setSiteName(p.name))
      .catch((err) => console.error('현장 이름 로드 실패:', err));
  }, [siteIdNum]);

  const fetcher = useCallback(
    () => OperationProcessService.getBySite(siteIdNum),
    [siteIdNum],
  );
  const saver = useCallback(
    (d: OperationProcessData) => OperationProcessService.updateBySite(siteIdNum, d),
    [siteIdNum],
  );

  // PDF — 워크플로는 데이터를 내부 보유하므로 클릭 시점에 서버에서 재조회해 생성.
  const handleDownloadPdf = useCallback(async () => {
    try {
      const d = await OperationProcessService.getBySite(siteIdNum);
      const v = buildWorkflowView(d.rows ?? []);
      const { html } = buildOperationProcessPdfHtml({
        title: '운영 프로세스',
        fixedSiteName: siteName || undefined,
        siteInfo: d.siteInfo ?? EMPTY_SITE_INFO,
        rows: d.rows ?? [],
        done: v.doneCount,
        total: v.totalCount,
        pct: v.percent,
      });
      const win = window.open('', '_blank', 'noopener,noreferrer');
      if (!win) {
        window.alert('팝업이 차단되어 PDF를 생성할 수 없습니다. 팝업을 허용해 주세요.');
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (err: any) {
      window.alert(`PDF 생성 실패: ${err?.message ?? err}`);
    }
  }, [siteIdNum, siteName]);

  // 템플릿에서 가져오기 — 단계를 템플릿으로 교체(현장 정보는 유지) 후 재로드.
  const handleImportTemplate = useCallback(async () => {
    const ok = window.confirm(
      '현재 작성된 단계를 모두 지우고 템플릿으로 덮어씁니다.\n현장 정보(위치/담당자 등)는 그대로 유지됩니다.\n계속하시겠습니까?'
    );
    if (!ok) return;
    setImporting(true);
    try {
      const [cur, tpl] = await Promise.all([
        OperationProcessService.getBySite(siteIdNum),
        OperationProcessService.getTemplate(),
      ]);
      // 템플릿의 ID 와 충돌 방지: 새 ID 부여 (기존 Table 편집 모드와 동일 규칙)
      const fresh = (tpl.rows ?? []).map((r) => ({
        ...r,
        id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }));
      await OperationProcessService.updateBySite(siteIdNum, { ...cur, rows: fresh });
      setWfKey((k) => k + 1);
    } catch (err: any) {
      window.alert(`템플릿 가져오기 실패: ${err?.message ?? err}`);
    } finally {
      setImporting(false);
    }
  }, [siteIdNum]);

  if (Number.isNaN(siteIdNum)) {
    return (
      <Container sx={{ p: 4 }}>
        <Typography color="error">유효하지 않은 프로젝트 ID 입니다.</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: wf.bg }}>
      {/* 상단 헤더 */}
      <Paper
        elevation={0}
        square
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <IconButton size="small" onClick={() => navigate(`/project/${projectId}`)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/projects')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon sx={{ fontSize: 16 }} />
            현장 목록
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/project/${projectId}`)}
            sx={{ cursor: 'pointer' }}
          >
            {siteName || '현장'}
          </Link>
          <Typography color="text.primary" fontWeight={600}>
            운영 프로세스
          </Typography>
        </Breadcrumbs>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadPdf}
          sx={{ textTransform: 'none' }}
        >
          PDF 다운로드
        </Button>
        {canEdit && (
          <Tooltip title="템플릿의 단계 목록으로 교체합니다." arrow>
            <Button
              variant="outlined"
              size="small"
              startIcon={importing ? <CircularProgress size={14} /> : <DownloadIcon />}
              disabled={importing}
              onClick={handleImportTemplate}
              sx={{ textTransform: 'none' }}
            >
              템플릿에서 가져오기
            </Button>
          </Tooltip>
        )}
      </Paper>

      {/* 본문 — 운영 템플릿과 동일한 워크플로 뷰 */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <OperationProcessWorkflow
          key={wfKey}
          eyebrow={siteName || '현장'}
          title="설치·구축 업무 절차"
          subtitle={
            canEdit
              ? '변경 사항은 즉시 저장됩니다.'
              : '조회 권한입니다. 단계 완료/편집은 admin/editor 권한이 필요합니다.'
          }
          fetcher={fetcher}
          saver={saver}
          readOnly={!canEdit}
          attachmentsProjectId={String(siteIdNum)}
        />
      </Box>
    </Box>
  );
}
